/**
 * NWF-178: shows the spin icon selected in the Magic 360 app settings as the
 * gallery thumbnail for the 360 slide.
 *
 * The app renders its own complete gallery server side - a slide track plus a
 * selector rail - inside `#magic-360-block`, regardless of the empty
 * `#nwf-magic360-slot` that nwf-magic360-init points it at. nwf-magic360.css
 * hides that rail, so the icon a merchant picks under "Spin icon" never
 * reaches the storefront. This copies it into our own thumbnail tile.
 *
 * Read from the DOM instead of hardcoded: the asset URL carries the app's
 * extension UUID and version
 * (`.../magic-360-226/assets/icon.spin.11_small.png`), and both change
 * whenever the app updates - a pinned URL would rot on the next release.
 *
 * The icon is addressed through `data-slide-id="spin"`, a semantic attribute,
 * rather than through the app's styling classes (the same reasoning as in
 * nwf-magic360.css). Two nodes carry that attribute - the slide, a `div`, and
 * the selector, an `a` - so the `a` qualifier is what makes the match
 * unambiguous.
 *
 * Purely additive: the tile ships with the theme's own `nwf-icon-360` as a
 * server rendered fallback, so a missing, renamed or moved app node leaves a
 * valid thumbnail behind instead of an empty box.
 */
(function () {
    var ICON_SELECTOR = 'a[data-slide-id="spin"] img';

    // Tiles still waiting for the swap; `data-nwf-app-icon` marks the done ones.
    var PENDING_TILE_SELECTOR = '.nwf-m360-thumb-tile:not([data-nwf-app-icon])';

    // This script is deferred, so the app block - which sits near the end of
    // <body> - is already parsed by the time the first attempt runs. The
    // observer only covers the app moving or re-rendering its block later, and
    // is bounded so it cannot watch the whole document for the page's lifetime.
    var OBSERVER_TIMEOUT = 10000;

    var observer = null;
    var observerTimer = null;

    function stopWatching() {
        if (observer) {
            observer.disconnect();
            observer = null;
        }
        window.clearTimeout(observerTimer);
    }

    function applyIcon() {
        var source = document.querySelector(ICON_SELECTOR);
        var src = source && source.getAttribute('src');
        if (!src) return false;

        var tiles = document.querySelectorAll(PENDING_TILE_SELECTOR);
        var applied = false;

        Array.prototype.forEach.call(tiles, function (tile) {
            var fallback = tile.querySelector('.nwf-m360-thumb-icon');
            if (!fallback) return;

            var icon = document.createElement('img');
            icon.className = 'nwf-m360-thumb-icon';
            icon.src = src;
            // The tile already carries a visually hidden label, so the image
            // itself stays out of the accessibility tree.
            icon.alt = '';
            icon.setAttribute('aria-hidden', 'true');

            tile.replaceChild(icon, fallback);
            tile.setAttribute('data-nwf-app-icon', '');
            applied = true;
        });

        return applied;
    }

    function watchForBlock() {
        if (typeof MutationObserver !== 'function') return;

        // Never leave a previous watch running: run() can be called again per
        // section load, and stacked document-wide observers are pure overhead.
        stopWatching();

        observer = new MutationObserver(function () {
            if (applyIcon()) stopWatching();
        });

        // `attributes` as well as `childList`: the app could insert the node
        // first and set its `src` afterwards, which no childList record covers.
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['src'],
        });

        observerTimer = window.setTimeout(stopWatching, OBSERVER_TIMEOUT);
    }

    function run() {
        // `shopify:section:load` fires for every section in the theme editor,
        // not just the gallery. Without this guard each unrelated reload would
        // arm another observer that has no tile left to fill and can only time
        // out. An empty result also means "already done", because a swapped
        // tile no longer matches the pending selector.
        if (!document.querySelector(PENDING_TILE_SELECTOR)) return;
        if (applyIcon()) return;

        watchForBlock();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run);
    } else {
        run();
    }

    // Theme editor only: a section reload re-renders the gallery, and the fresh
    // tile carries the fallback icon again.
    document.addEventListener('shopify:section:load', run);
})();
